import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import DashboardLayout from './layouts/DashboardLayout';
import ClientesPage from './features/clients/ClientesPage';
import DashboardPage from './features/dashboard/DashboardPage';
import ClientDetailPage from './features/clients/ClientDetailPage';
import ProdutosPage from './features/products/ProdutosPage';
import AwsProdutosCatalog from './features/products/AwsProdutosCatalog';
import AwsPedidosPage from './features/orders/AwsPedidosPage';
import AwsOrderFormPage from './features/orders/AwsOrderFormPage';
import PedidosPage from './features/orders/PedidosPage';
import RastreioPage from './features/rastreio/RastreioPage';
import ComplaintsPage from './features/complaints/ComplaintsPage';
import LoginPage from './features/auth/LoginPage';
import CloudLoginPage from './features/auth/CloudLoginPage';
import ProtectedRoute from './components/ProtectedRoute';
import SalesChessPage from './features/sales-chess/SalesChessPage';
import AdminPage from './features/admin/AdminPage';
import AdminRoute from './components/AdminRoute';
import SampleRoute from './components/SampleRoute';
import RegionsPage from './features/regions/RegionsPage';
import SalesRepPage from './features/sales-rep/SalesRepPage';
import SampleAnalysisPage from './features/sample-analysis/SampleAnalysisPage';
import SampleOrderDetailsPage from './features/sample-analysis/SampleOrderDetailsPage';
import SampleEvaluationFormPage from './features/sample-analysis/SampleEvaluationFormPage';
import StageEvaluationPage from './features/avaliacao/StageEvaluationPage';
import DashboardAmostrasPage from './features/marketing/DashboardAmostrasPage';
import SupervisorRoute from './components/SupervisorRoute';
import SupervisorApprovalPage from './features/supervisor-approval/SupervisorApprovalPage';
import SupervisorOrderDetailsPage from './features/supervisor-approval/SupervisorOrderDetailsPage';
import { useSessionTimeout } from './hooks/useSessionTimeout';
import SessionExpiredNotification from './components/SessionExpiredNotification';
import useHeartbeat from './hooks/useHeartbeat';
import { useAuthStore } from './stores/authStore';
import { backendHost } from './services/apiConfig';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 0,                    // 0: sempre busca fresco
      gcTime: 10 * 60 * 1000,          // 10 min: mantém cache para navegação suave
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// Detecta ambiente AWS consultando /api/ping em runtime
const useIsCloudEnv = () => {
    const [isCloud, setIsCloud] = useState<boolean | null>(null);
    useEffect(() => {
        const base = backendHost ? `${backendHost}/api` : '/api';
        fetch(`${base}/ping`)
            .then(r => r.json())
            .then(d => setIsCloud(d.isAws === true))
            .catch(() => setIsCloud(false));
    }, []);
    return isCloud;
};

const LoginRouter: React.FC = () => {
    const isCloud = useIsCloudEnv();
    if (isCloud === null) return null; // loading
    return isCloud ? <CloudLoginPage /> : <LoginPage />;
};

const App: React.FC = () => {
  // Simula limite de sessão por inatividade
  const { showNotification, hideNotification } = useSessionTimeout({
    timeout: 8 * 60 * 60 * 1000, // 8 horas de inatividade
  });

  // Heartbeat a cada 30s para manter sessão como 'online'
  const isAuthenticated = useAuthStore(s => s.isAuthenticated);
  useHeartbeat(isAuthenticated);

  return (
    <QueryClientProvider client={queryClient}>
      <SessionExpiredNotification
        show={showNotification}
        onClose={hideNotification}
      />
      <Router>
        <Routes>
          <Route path="/login" element={<LoginRouter />} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<DashboardPage />} />
            <Route path="clientes/:clientId" element={<ClientDetailPage />} />
            <Route path="produtos" element={<ProdutosPage />} />
            <Route path="pedidos" element={<PedidosPage />} />
            <Route path="rastreio" element={<RastreioPage />} />

            {/* Rotas Exclusivas da AWS */}
            <Route path="aws-produtos" element={<AwsProdutosCatalog />} />
            <Route path="aws-pedidos" element={<AwsPedidosPage />} />
            <Route path="aws-novo-pedido" element={<AwsOrderFormPage />} />
            <Route path="aws-editar-pedido/:id" element={<AwsOrderFormPage />} />

            <Route path="reclamacoes" element={<ComplaintsPage />} />
            <Route path="Clientes" element={<ClientesPage />} />
            <Route path="xadrez-vendas" element={<SalesChessPage />} />
            <Route path="vendas-regiao" element={<RegionsPage />} />
            <Route path="vendedores" element={<SalesRepPage />} />
            <Route
              path="amostras-pedidos"
              element={
                <SampleRoute>
                  <SampleAnalysisPage />
                </SampleRoute>
              }
            />
            <Route
              path="amostras-pedidos/:id"
              element={
                <SampleRoute>
                  <SampleOrderDetailsPage />
                </SampleRoute>
              }
            />
            <Route
              path="amostras-pedidos/:id/avaliacao"
              element={
                <SampleRoute>
                  <StageEvaluationPage />
                </SampleRoute>
              }
            />
            <Route
              path="amostras-pedidos/:id/avaliar"
              element={
                <SampleRoute>
                  <SampleEvaluationFormPage />
                </SampleRoute>
              }
            />
            <Route
              path="amostras-dashboard"
              element={
                <SampleRoute>
                  <DashboardAmostrasPage />
                </SampleRoute>
              }
            />
            <Route
              path="aprovacao-pedidos"
              element={
                <SupervisorRoute>
                  <SupervisorApprovalPage />
                </SupervisorRoute>
              }
            />
            <Route
              path="aprovacao-pedidos/:id"
              element={
                <SupervisorRoute>
                  <SupervisorOrderDetailsPage />
                </SupervisorRoute>
              }
            />
            <Route
              path="admin"
              element={
                <AdminRoute>
                  <AdminPage />
                </AdminRoute>
              }
            />
          </Route>
        </Routes>
      </Router>
    </QueryClientProvider>
  );
};

export default App;